import { Popover } from '@servicecompressionllc/sc-design-system';
import { Fragment } from 'react';
import { forkDepth, type StepPath } from './definitionEdit.js';
import { type StepListContext } from './stepTypes.js';

export const AddStepMenu = ({
  containerPath,
  ctx,
  compact,
}: {
  containerPath: StepPath;
  ctx: StepListContext;
  compact?: boolean;
}) => {
  const depth = forkDepth(containerPath);
  const groups = new Map<string, StepListContext['stepTypes']>();
  for (const step of ctx.stepTypes) {
    const group = groups.get(step.group) ?? [];
    group.push(step);
    groups.set(step.group, group);
  }
  return (
    <Popover className={compact ? undefined : 'block w-full'}>
      <Popover.Trigger
        className={
          compact
            ? 'text-xs text-primary hover:underline'
            : 'w-full rounded-lg border border-dashed border-outline-strong px-3 py-2 text-sm text-ink-muted transition-colors hover:border-ink-subtle hover:text-ink'
        }
      >
        + Add step
      </Popover.Trigger>
      {/* The trigger is a full-width dashed button and Popover.Content is
          `min-w-full`, so a class-based width loses to it — the menu grew to
          the whole column. Inline width wins. The height cap goes on the list,
          because Content computes its own maxHeight from the viewport and a
          seven-row menu is allowed to fill it. */}
      <Popover.Content
        aria-label="Step types"
        className="max-w-[90vw] [&_.bg-info-soft]:bg-[color-mix(in_srgb,var(--info-soft)_25%,var(--surface-elevated))] [&_.border-info-border]:border-[color-mix(in_srgb,var(--info-border)_25%,var(--outline))] [&_.text-info]:text-[color-mix(in_srgb,var(--info)_40%,var(--ink-muted))]"
        style={{ width: '21rem', minWidth: '21rem' }}
      >
        <ul className="max-h-[24rem] overflow-y-auto pb-1">
          {ctx.stepTypes.length === 0 && (
            <li className="px-3 py-2 text-sm text-ink-muted">No steps are available.</li>
          )}
          {Array.from(groups, ([title, types]) => (
            <Fragment key={title}>
              <Popover.Section>{title}</Popover.Section>
              {types.map((t) => {
                const blocked = t.fork && depth >= 2;
                return (
                  <Popover.Item
                    key={t.type}
                    hint={t.hint}
                    disabled={blocked}
                    title={
                      blocked
                        ? 'This already splits twice — start another workflow instead.'
                        : undefined
                    }
                    onClick={() => ctx.onInsert(containerPath, t.type)}
                  >
                    {t.label}
                  </Popover.Item>
                );
              })}
            </Fragment>
          ))}
        </ul>
      </Popover.Content>
    </Popover>
  );
};
